import {Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService} from "primeng/api";
import {DialogService} from "primeng/dynamicdialog";
import {EmployeeRoleEnum} from '../../enums/employee-role.enum';
import {AddOrEditManagerEmployeeComponent} from "./add-or-edit-manager-employee/add-or-edit-manager-employee.component";
import {DeleteManagerEmployeeComponent} from "./delete-manager-employee/delete-manager-employee.component";
import {AssignShiftComponent} from "./assign-shift/assign-shift.component";
import {TranslateService} from "@ngx-translate/core";
import * as moment from "moment";
import {FullCalendarComponent} from "@fullcalendar/angular";
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {ManagerEmployeeModel} from "../../models/employees/manager-employee.model";
import {EmployeeLogTypes} from "../../enums/employee-log-type.enum";
import {Subject, Subscription, takeUntil} from "rxjs";
import {ManagerEmployeeService} from "../../services/employees/manager-employees.service";
import {
  CurrentVenueHelperService
} from "../../../share-modul/services/current-venue-helper/current-venue.helper.service";
import {EmployeeLogModel} from "../../models/shifts/employee-log.model";
import {CalendarOptions} from '@fullcalendar/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from "@angular/router";

@Component({
  selector: 'app-manager-employees',
  templateUrl: './manager-employees.component.html',
  styleUrls: ['./manager-employees.component.scss']
})
export class ManagerEmployeesComponent implements OnInit, OnDestroy {

  @ViewChildren(FullCalendarComponent) calendars: QueryList<FullCalendarComponent>;
  public options: { [id: string] : CalendarOptions; };
  public headerCalendarOptions: any;
  public selectedMonthYear: Date = new Date();
  public unsubscribe: Subject<void> = new Subject<void>();
  public listAllEmployees: Array<ManagerEmployeeModel> = new Array<ManagerEmployeeModel>();
  public imagePath: string = './assets/images/logo/profile-picture-placeholder.png'
  public items: { [name: string]: MenuItem[] } = {};
  public EmployeeRoleEnum = EmployeeRoleEnum;
  public venueId: string | null;
  public selectedDate: number = Date.now();
  public venueIdUpdateSubscription: Subscription;

  private colors: string[] = ['#F7CC7F', '#FCAFFC', '#ADDFFF', '#96D9C9'];
  private venueColorMap: Map<string, string> = new Map<string, string>();
  private selectedColors: string[] = [];
  private languageChangeSubscription: Subscription;
 
  
  constructor(
    public dialogService: DialogService,
    private messageService: MessageService,
    public translate: TranslateService,
    public employeeService: ManagerEmployeeService,
    public currentVenueHelper: CurrentVenueHelperService,
    private confirmationService: ConfirmationService,
    public router: Router,
    private route: ActivatedRoute
  ) {
    this.translate.stream('HEADER.APP_LANGUAGE').subscribe(() => {
      this.addFunctionToEmployees();
    });
  }

  ngOnInit(): void {
    this.headerCalendarOptions = {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      firstDay: 1,
      initialDate: new Date(),
      headerToolbar: {
        left: '',
        center: 'title',
        right: ''
      },
      titleFormat: (args: any) => this.titleFormat(args),
      dayHeaderContent: (args: any) => this.dayHeaderContent(args),
      initialView: 'dayGridWeek',
      height: 80,
      nowIndicator: true
    };

    this.route.params.subscribe(params => {
      if(params['venue-id'] != null){
        this.venueId = params['venue-id'];
        this.currentVenueHelper.updateCurrentVenueIdDropdown(params['venue-id']);
        let date = this.calendars?.length > 0 ?  moment(this.calendars.get(0)?.getApi().getDate()).format('MM/DD/YYYY'):moment(this.selectedDate).format('MM/DD/YYYY')
        this.getEmployees(params['venue-id'], date);
      }
    });

    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      if (this.calendars.length > 0 && this.venueId !== null) {
        let header = this.calendars.get(0)?.getApi();
        // header?.setOption('dayHeaderContent', (args: any) => this.dayHeaderContent(args));
        header?.setOption('titleFormat', (args: any) => this.titleFormat(args));
        this.getEmployees(this.venueId, moment(this.calendars.get(0)?.getApi().getDate()).format('MM/DD/YYYY'));
      }
    });
  }

  public onDayClicked(event: any, date: Date, employee: ManagerEmployeeModel, eventClick: boolean = false): void {
    if (moment(date).startOf('day').isSame(moment().startOf('day')) && eventClick){
      this.confirmationService.confirm({
        target: event.target,
        acceptLabel: this.translate.instant("HEADER.LOGOUT"),
        defaultFocus: 'reject',
        acceptIcon: 'pi c-icons-black logout-icon',
        rejectLabel: this.translate.instant("MANAGER_EMPLOYEES.CANCEL_SHIFT"),
        acceptButtonStyleClass: 'remote-logout-button-style-class',
        rejectButtonStyleClass: 'remote-logout-button-style-class',
        acceptVisible: employee.venueEmployeeTypeId == EmployeeRoleEnum.Waiter,
        accept: () => {
          //confirm action
          this.employeeService.logoutEmployee(employee.id).pipe(takeUntil(this.unsubscribe))
            .subscribe({
              next: _ => {
                this.messageService.add({ severity: 'success', summary: this.translate.instant('MANAGER_EMPLOYEES.LOGGED_OUT'), life: 3000 });
              },
              error: _ => {
                this.messageService.add({ severity: 'error', summary: "System error", life: 2000 });
              }
            });
        },
        reject: () => {
          //reject action
          const logData = employee.logs.find(x => moment(x.dateTime).format('MM/dd/YYYY') === moment(date).format('MM/dd/YYYY'));
          if (logData) {
            this.removeCurrentEmployeeLog(logData.id);
          }
        }
      });
    } else {
      const ref = this.dialogService.open(AssignShiftComponent, {
        data: {
          id: employee.id,
          logData: employee.logs.find(x => moment(x.dateTime).format('MM/dd/YYYY') === moment(date).format('MM/dd/YYYY')),
          date: date,
          employeeFirstName: employee.firstName,
          employeeLastName: employee.lastName,
          venueId: this.venueId
        },
        header: this.translate.instant('MANAGER_EMPLOYEES.ASSIGN_SHIFT'),
        width: '350px',
        styleClass: 'ml_200, mb-15'
      });
      ref.onClose.subscribe((item) => {
        if (item) {
          if (this.venueId !== null) {
            this.getEmployees(this.venueId, moment(this.calendars.get(0)?.getApi().getDate()).format('MM/DD/YYYY'));
          }
        }
      });
    }
  }

  public removeCurrentEmployeeLog(id: string): void {
    this.employeeService.removeEmployeeLog(id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: _ => {
          if (this.venueId !== null) {
            this.getEmployees(this.venueId, moment(this.calendars.get(0)?.getApi().getDate()).format('MM/DD/YYYY'));
          }
        },
        error: errorResponse => {
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  editEmployee(employee: ManagerEmployeeModel): void {
    const ref = this.dialogService.open(AddOrEditManagerEmployeeComponent, {
      data: {
        employee: Object.assign({}, employee)
      },
      header: this.translate.instant('MANAGER_EMPLOYEES.EDIT_EMPLOYEE'),
      width: '685px',
      styleClass: 'ml_200, mb_15'
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        if (this.venueId !== null) {
          this.getEmployees(this.venueId, moment(this.calendars.get(0)?.getApi().getDate()).format('MM/DD/YYYY'));
          this.messageService.add({ severity: 'success', summary: this.translate.instant('MANAGER_EMPLOYEES.EMPLOYEE_EDITED'), life: 3000 });
        }
      }
    });
  }

  deleteEmployee(userId: string): void {
    const ref = this.dialogService.open(DeleteManagerEmployeeComponent, {
      data: {
        id: userId,
        venueId: this.venueId
      },
      header: this.translate.instant('MANAGER_EMPLOYEES.DELETE_EMPLOYEE'),
      width: '685px',
      styleClass: 'ml_200, mb_15'
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        if (this.venueId !== null) {
          this.getEmployees(this.venueId, moment(this.calendars.get(0)?.getApi().getDate()).format('MM/DD/YYYY'));
          this.messageService.add({ severity: 'success', summary: this.translate.instant('MANAGER_EMPLOYEES.EMPLOYEE_DELETED'), life: 3000 });
        }
      }
    });
  }

  getEmployees(venueId: string, dateTime: string) {
    this.listAllEmployees = new Array<ManagerEmployeeModel>();
    if (this.venueId) {
      this.employeeService.listAllEmployeesForVenue(this.venueId, dateTime)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (response) => {
            this.listAllEmployees = response;
            this.initCalendars(response, new Date(dateTime));
            this.addFunctionToEmployees();
          },
          error: errorResponse => {
            this.messageService.add({severity: 'error', summary: "System error", life: 2000});
          }
        });
    }
  }

  public prevWeek(): void {
    this.calendars.forEach(calendar => calendar.getApi().prev());
    if (this.venueId !== null) {
      this.getEmployees(this.venueId, moment(this.calendars.get(0)?.getApi().getDate()).format('MM/DD/YYYY'));
    }
  }

  public nextWeek(): void {
    this.calendars.forEach(calendar => calendar.getApi().next());
    if (this.venueId !== null) {
      this.getEmployees(this.venueId, moment(this.calendars.get(0)?.getApi().getDate()).format('MM/DD/YYYY'));
    }
  }

  public goToDate(selection: Date): void {
    this.selectedMonthYear = selection;
    if (this.venueId !== null) {
      this.getEmployees(this.venueId, moment(new Date(this.selectedMonthYear.getFullYear(), this.selectedMonthYear.getMonth())).format('MM/DD/YYYY'));
    }
    if (this.calendars.length > 0) {
      if (this.selectedMonthYear !== undefined) {
        this.calendars.forEach(calendar => calendar.getApi().gotoDate(new Date(this.selectedMonthYear.getFullYear(), this.selectedMonthYear.getMonth())));
      }
    }
  }

  public goToToday(): void {
    if (this.venueId !== null) {
      this.getEmployees(this.venueId, moment(this.selectedDate).format('MM/DD/YYYY'));
    }
    if (this.calendars.length > 0) {
      this.calendars.forEach(calendar => calendar.getApi().gotoDate(new Date()));
    }
  }

  public initCalendars(response: any[], date: Date): void {
    this.options = {};
    this.headerCalendarOptions = {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      firstDay: 1,
      initialDate: date,
      headerToolbar: {
        left: '',
        center: 'title',
        right: ''
      },
      titleFormat: (args: any) => this.titleFormat(args),
      dayHeaderContent: (args: any) => this.dayHeaderContent(args),
      initialView: 'dayGridWeek',
      height: 80,
      nowIndicator: true
    };
    response.forEach((employee) => {
      this.options[employee.id] = {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialDate: date,
        firstDay: 1,
        dayHeaders: false,
        headerToolbar: false,
        initialView: 'dayGridWeek',
        height: 38,
        dayCellContent: (arg: any) => {
          let result = employee.logs.filter((log: any) => new Date(log.dateTime).getTime() === new Date(arg.date).getTime());
          if(result.length == 0) {
            let buttonElement = document.createElement('button');
            buttonElement.textContent = this.translate.instant("MANAGER_EMPLOYEES.ADD_SHIFT")
            buttonElement.className = "select_shift_btn";

            buttonElement.addEventListener("click", (e: Event) => {
              e.stopPropagation();
              this.onDayClicked(e, arg.date, employee);

            });
            let arrayOfDomNodes = [buttonElement];
            return {domNodes: arrayOfDomNodes};   
        }
        return;
        },
        eventContent: (arg: any) => {
          let buttonElement = document.createElement('div');

          if (arg.event.extendedProps.logType !== EmployeeLogTypes.Shift) {
            buttonElement.className = "free_sick_day_config";
            buttonElement.innerHTML = arg.event.title;
          } else {
            buttonElement.className = "shift_day_config";
            buttonElement.innerHTML = arg.event.title + "<br><b>" + arg.event.extendedProps.venueName + "</b>";
            buttonElement.title = arg.event.extendedProps.venueName;
          }

          let arrayOfDomNodes = [buttonElement];
          return {domNodes: arrayOfDomNodes};
        },
        eventClick: (info: any) => {
          if (info.event.extendedProps.venueId === this.venueId) {
            this.onDayClicked(info.jsEvent, info.event.start, employee, true);
          }
        },
        events: employee.logs.map((x: EmployeeLogModel) => (
          {
            start: moment(x.dateTime).format('YYYY-MM-DD'),
            title: this.buildDescription(x.employeeLogTypeId, x.from, x.to),
            extendedProps: {
              venueId: x.venueId,
              venueName: x.venueName,
              logType: x.employeeLogTypeId
            },
            backgroundColor: x.employeeLogTypeId !== EmployeeLogTypes.Shift ? 'black' : this.chooseVenueEventsColor(x.venueId),
            borderColor: x.employeeLogTypeId !== EmployeeLogTypes.Shift ? 'default' : this.chooseVenueEventsColor(x.venueId),
            textColor: 'black'
          }))
      }
    });
  }

  addFunctionToEmployees(): void {
    this.listAllEmployees.forEach((employee) => {

      this.items[employee.id] = [{
          label: this.translate.instant('EDIT'),
          command: () => {
            this.editEmployee(employee);
          }
        },
        {
          label: this.translate.instant('DELETE'),
          command: () => {
            this.deleteEmployee(employee.id);
          }
        }];
    });
  }

  ngOnDestroy(): void {
    if (this.venueIdUpdateSubscription) {
      this.venueIdUpdateSubscription.unsubscribe();
    }
    if (this.languageChangeSubscription) {
      this.languageChangeSubscription.unsubscribe();
    }
  }


  private chooseVenueEventsColor(venueId: string): string {
    if (this.selectedColors.length === this.colors.length) {
      this.selectedColors = [];
    }
    let color = this.venueColorMap.get(venueId);
    if (color !== undefined) {
      return color;
    }
    for (let i = 0; i < this.colors.length; i++) {
      if (!this.selectedColors.includes(this.colors[i])) {
        color = this.colors[i];
        this.venueColorMap.set(venueId, color);
        this.selectedColors.push(color);
        return color;
      }
    }
    return '';
  }

  private buildDescription(employeeLogTypeId: EmployeeLogTypes, from: string, to: string) {
    switch (employeeLogTypeId) {
      case EmployeeLogTypes.FreeDay:
        return this.translate.instant("MANAGER_EMPLOYEES.FREE_DAY");
      case EmployeeLogTypes.SickLeave:
        return this.translate.instant("MANAGER_EMPLOYEES.SICK_DAY");
      default:
        return this.translate.instant("MANAGER_EMPLOYEES.FROM") + ' ' + from.trim() + 'h-' + to.trim() + 'h';
    }
  }

  dayHeaderContent(args: any) {
    moment.locale(this.translate.currentLang);
    return moment(args.date).format('ddd, D');
  }

  titleFormat(args: any) {
    moment.locale(this.translate.currentLang);
    let date = moment(args.date);
    let lastWeekOfMonth = date.isoWeek() >= date.endOf('month').isoWeek();
    let isSunday = date.day() === 0;

    if (lastWeekOfMonth && !isSunday) {
      let nextMonth = date.clone().add(1, 'month');
      if (date.year() !== nextMonth.year()) {
        return date.format('MMMM YYYY') + ' - ' + nextMonth.format('MMMM YYYY');
      }
      return date.format('MMMM') + ' - ' + nextMonth.format('MMMM YYYY');
    }

    return moment(args.date).format('MMMM yyyy');
  }

}
